import type { CoverTone } from '@/components/workbooks/BookCover';

export interface Workbook {
  slug: string;
  title: string;
  subtitle: string;
  bullets: string[];
  pages: number;
  price: number;
  /** Stripe price lookup key used at checkout */
  priceId: string;
  category: string;
  relatedPath: string;
  coverTone: CoverTone;
  /** Article categories this workbook is the best match for */
  matches: string[];
  /** Who this title is written for */
  forYou: string;
  /** Who should skip it — the honesty signal */
  notForYou: string;
}


export const WORKBOOK_CURRENCY = 'USD';

export const workbooks: Workbook[] = [
  {
    slug: 'is-nomad-living-right-for-you',
    title: 'Is Nomad Living Right For You',
    subtitle: 'An honest self-assessment before you sell anything.',
    bullets: [
      'A 40-question readiness audit covering money, health, relationships and work',
      'Three deal-breaker exercises most people discover far too late',
      'A written decision page you can revisit in six months',
    ],
    pages: 34,
    price: 29,
    priceId: 'workbook_is_nomad_living_right_for_you_onetime',
    category: 'Start here',
    relatedPath: '/article/van-life-basics-complete-guide',
    coverTone: 'forest',
    matches: ['mobile-living'],
    forYou: 'You are seriously considering leaving conventional housing and want to know, on paper, whether it holds up.',
    notForYou: 'You have already committed and bought the vehicle — start with Choose Your Rig instead.',
  },
  {
    slug: 'choose-your-rig',
    title: 'Choose Your Rig',
    subtitle: 'Van, boat, cabin or tent — narrowed down on paper first.',
    bullets: [
      'Side-by-side scoring sheets for the four main living formats',
      'Payload, mooring and land-access checks specific to each option',
      'A shortlist worksheet that ends with one rig and a target budget',
    ],
    pages: 42,
    price: 34,
    priceId: 'workbook_choose_your_rig_onetime',
    category: 'Decision',
    relatedPath: '/article/van-life-payload-weight-limits-guide',
    coverTone: 'ink',
    matches: ['mobile-living'],
    forYou: 'You know you are going, but van, boat, cabin and tent are all still on the table.',
    notForYou: 'You already own the rig. Nothing here will change that decision.',
  },
  {
    slug: 'the-build-before-the-build',
    title: 'The Build Before The Build',
    subtitle: 'Plan the layout, sequence and spend before the first cut.',
    bullets: [
      'Scale grid pages for floor plan sketching at 1:20',
      'Build sequence planner so insulation never gets trapped behind panels',
      'Cost tracker with columns for quoted, actual and forgotten items',
    ],
    pages: 56,
    price: 39,
    priceId: 'workbook_the_build_before_the_build_onetime',
    category: 'Build',
    relatedPath: '/article/off-grid-living-basics-complete-guide',
    coverTone: 'clay',
    matches: ['off-grid-homes'],
    forYou: 'You are about to convert or build something and want the sequence right before the first cut.',
    notForYou: 'You are buying a finished, ready-to-live rig.',
  },
  {
    slug: 'the-nomad-money-reset',
    title: 'The Nomad Money Reset',
    subtitle: 'Runway, real monthly cost and the number you actually need.',
    bullets: [
      'Twelve-month runway model with seasonal cost variation built in',
      'Hidden-cost inventory: insurance, storage, repairs, border admin',
      'A break-even worksheet comparing your current rent against life on the road',
    ],
    pages: 48,
    price: 39,
    priceId: 'workbook_the_nomad_money_reset_onetime',
    category: 'Money',
    relatedPath: '/tools',
    coverTone: 'brass',
    matches: [],
    forYou: 'You want a runway number you can defend rather than a hopeful guess.',
    notForYou: 'Your income is fixed, salaried and unaffected by where you sleep.',
  },
  {
    slug: 'work-from-anywhere-for-real',
    title: 'Work From Anywhere For Real',
    subtitle: 'Income, connectivity and a work rhythm that survives travel.',
    bullets: [
      'Connectivity planning sheets for cellular, satellite and marina wifi',
      'A weekly rhythm template that separates driving days from deep work',
      'Income stress test: what happens to your month if one client leaves',
    ],
    pages: 44,
    price: 34,
    priceId: 'workbook_work_from_anywhere_for_real_onetime',
    category: 'Work',
    relatedPath: '/article/boat-living-basics-complete-guide',
    coverTone: 'slate',
    matches: ['boat-living'],
    forYou: 'Your income has to survive travel days, weak signal and unpredictable weeks.',
    notForYou: 'You are retired or otherwise not working while on the road.',
  },
  {
    slug: 'small-space-real-life',
    title: 'Small Space Real Life',
    subtitle: 'Storage, routines and the daily reality of living with less.',
    bullets: [
      'A room-by-room possession audit with keep, store and sell columns',
      'Storage mapping pages for every cubic foot you actually have',
      'Routine worksheets for cooking, laundry and drying wet gear',
    ],
    pages: 38,
    price: 29,
    priceId: 'workbook_small_space_real_life_onetime',
    category: 'Daily life',
    relatedPath: '/article/camping-basics-complete-guide',
    coverTone: 'forest',
    matches: ['camping'],
    forYou: 'The decision is made and the daily reality — storage, laundry, wet gear — is what worries you.',
    notForYou: 'You are still deciding whether to go at all.',
  },
  {
    slug: 'family-nomad-life',
    title: 'Family Nomad Life',
    subtitle: 'Kids, schooling and space — planned before you commit.',
    bullets: [
      'Schooling comparison sheets: homeschool, online, and local enrolment',
      'Space planning for sleeping, study and the arguments nobody warns you about',
      'Safety, healthcare and paperwork checklists for crossing borders with children',
    ],
    pages: 62,
    price: 45,
    priceId: 'workbook_family_nomad_life_onetime',
    category: 'Family',
    relatedPath: '/article/off-grid-living-basics-complete-guide',
    coverTone: 'clay',
    matches: ['off-grid-homes'],
    forYou: 'You are moving with children and need schooling, space and paperwork settled first.',
    notForYou: 'You are travelling solo or as a couple.',
  },
];

export const BUNDLE_PRICE = 149;
export const BUNDLE_PRICE_ID = 'workbook_complete_library_onetime';

export const workbooksTotalValue = workbooks.reduce((sum, w) => sum + w.price, 0);

export const BUNDLE_SAVING = workbooksTotalValue - BUNDLE_PRICE;

/**
 * Open promotion banner above the collection. Turned off while the discount
 * is reserved for people who leave an email address (see `welcomeOffer`).
 */
export const workbookPromotion = {
  active: false,
  code: 'LAUNCH20',
  headline: 'Launch offer',
  detail: '20% off every workbook and the full collection',
  note: 'Enter the code in the payment window. Applies until the launch window closes.',
};

/**
 * The welcome discount. Not published on the site — it is sent in the sample
 * pack email, so the address is what the reader trades for it. The coupon
 * itself lives in Stripe; this only controls the wording.
 */
export const welcomeOffer = {
  code: 'NOMAD20',
  percentOff: 20,
  /** Last day the code can be redeemed, inclusive. */
  endsOn: '2026-08-31',
  endsLabel: '31 August',
};

/** False once the end date has passed, so the copy retires itself. */
export const isWelcomeOfferActive = (now: Date = new Date()) =>
  now <= new Date(`${welcomeOffer.endsOn}T23:59:59Z`);

/**
 * Volume discount ladder for multi-title carts. Mirrors
 * `supabase/functions/_shared/pricing.ts` — the server is the source of
 * truth for what is actually charged; this is for display only.
 */
export const VOLUME_TIERS = [
  { min: 4, percentOff: 20 },
  { min: 3, percentOff: 15 },
  { min: 2, percentOff: 10 },
] as const;

export const tierForCount = (count: number) =>
  VOLUME_TIERS.find((t) => count >= t.min) ?? null;

/** Next discount level and how many titles are missing to reach it. */
export const nextTierForCount = (
  count: number,
): { needed: number; percentOff: number } | null => {
  const upcoming = [...VOLUME_TIERS]
    .sort((a, b) => a.min - b.min)
    .find((t) => count < t.min);
  if (!upcoming) return null;
  return { needed: upcoming.min - count, percentOff: upcoming.percentOff };
};

export const getWorkbookBySlug = (slug: string): Workbook | undefined =>
  workbooks.find((w) => w.slug === slug);

/**
 * Deterministic complementary title for each workbook. Used for the
 * "pairs well with" line and to order cart suggestions.
 */
export const PAIRINGS: Record<string, string> = {
  'is-nomad-living-right-for-you': 'choose-your-rig',
  'choose-your-rig': 'the-build-before-the-build',
  'the-build-before-the-build': 'the-nomad-money-reset',
  'the-nomad-money-reset': 'work-from-anywhere-for-real',
  'work-from-anywhere-for-real': 'small-space-real-life',
  'small-space-real-life': 'family-nomad-life',
  'family-nomad-life': 'is-nomad-living-right-for-you',
};

export const getPairing = (slug: string): Workbook | undefined =>
  getWorkbookBySlug(PAIRINGS[slug] ?? '');

/** Three-title starting points, priced at the 15% volume tier. */
export interface PresetSet {
  id: string;
  name: string;
  note: string;
  slugs: string[];
}

export const PRESET_SETS: PresetSet[] = [
  {
    id: 'planning',
    name: 'The planning set',
    note: 'Decide, pick a rig, and get the money honest before anything is bought.',
    slugs: ['is-nomad-living-right-for-you', 'choose-your-rig', 'the-nomad-money-reset'],
  },
  {
    id: 'build',
    name: 'The build set',
    note: 'For the ones already committed to converting or building something.',
    slugs: ['choose-your-rig', 'the-build-before-the-build', 'small-space-real-life'],
  },
  {
    id: 'road',
    name: 'Life on the road',
    note: 'Income, daily rhythm and living in the space once you are actually out there.',
    slugs: ['work-from-anywhere-for-real', 'small-space-real-life', 'family-nomad-life'],
  },
];

export const presetSetPrice = (slugs: string[]) => {
  const subtotal = slugs.reduce(
    (sum, slug) => sum + (getWorkbookBySlug(slug)?.price ?? 0),
    0,
  );
  const percentOff = tierForCount(slugs.length)?.percentOff ?? 0;
  const total = Math.round(subtotal * (1 - percentOff / 100));
  return { subtotal, percentOff, total, saving: subtotal - total };
};


export const workbookFAQs = [
  {
    question: 'What format are the workbooks in?',
    answer:
      'Print-ready PDF, sized for both A4 and US Letter. Every page is designed to be written on, either printed or filled in digitally on a tablet. They are working documents, not another guide to skim.',
  },
  {
    question: 'How soon do I get the files?',
    answer:
      'Immediately. All seven titles are published, so the download link appears on the confirmation page the moment payment clears, and the same private library link is emailed to you for later.',
  },

  {
    question: 'Do the workbooks repeat what is already free on the site?',
    answer:
      'No. The articles explain how things work. The workbooks make you apply it to your own numbers, your own rig and your own timeline. They reference the guides where relevant instead of restating them.',
  },
  {
    question: 'Will they be updated?',
    answer:
      'Yes. Cost figures, regulations and gear recommendations change. Buyers receive revised editions at no extra cost, the same way the articles on this site are revised rather than replaced.',
  },
];
export const getWorkbookForCategory = (category: string, offset = 0): Workbook => {
  const matched = workbooks.filter((w) => w.matches.includes(category));
  const pool = matched.length > 0 ? matched : workbooks;
  const extra = workbooks.filter((w) => !pool.includes(w));
  const ordered = [...pool, ...extra];
  return ordered[offset % ordered.length];
};

/**
 * Short format line shown under each cover. All titles are published,
 * so this describes the artefact rather than a release date.
 */
export const getFormatLine = (slug: string): string => {
  const w = workbooks.find((item) => item.slug === slug);
  return w ? `${w.pages} pages · print-ready PDF` : 'Print-ready PDF';
};


/**
 * Outcome-first positioning. Each line names the expensive decision the
 * workbook is meant to prevent, so the price is judged against that cost
 * rather than against a page count.
 */
export const WORKBOOK_OUTCOMES: Record<string, { lead: string; anchor: string }> = {
  'is-nomad-living-right-for-you': {
    lead: 'Find out whether this life actually fits you before you sell the furniture.',
    anchor: 'Before you unwind a home worth years of savings, spend one evening testing the assumption.',
  },
  'choose-your-rig': {
    lead: 'Know what type of rig fits your real life before you spend thousands buying one.',
    anchor: 'A rig that does not fit your life is a $20,000–$60,000 mistake. This costs $34.',
  },
  'the-build-before-the-build': {
    lead: 'Make the expensive decisions on paper before making them in plywood, wiring and steel.',
    anchor: 'Rebuilding a wrong layout means tearing out work you already paid for twice.',
  },
  'the-nomad-money-reset': {
    lead: 'Find out whether your budget survives a bad month, not just an Instagram month.',
    anchor: 'Running out of runway 400 miles from home is the most common reason people quit.',
  },
  'work-from-anywhere-for-real': {
    lead: 'Test whether your income survives travel days, weak signal and unpredictable weeks.',
    anchor: 'One lost client on the road costs more than every workbook here combined.',
  },
  'small-space-real-life': {
    lead: 'Know how the daily reality feels — storage, laundry, wet gear — before you commit to it.',
    anchor: 'Most people leave the lifestyle over daily friction, not over the big decisions.',
  },
  'family-nomad-life': {
    lead: 'Settle schooling, space and paperwork on paper before moving children into it.',
    anchor: 'A schooling or paperwork gap discovered mid-journey ends the trip early.',
  },
};

export const getOutcome = (slug: string) =>
  WORKBOOK_OUTCOMES[slug] ?? { lead: getWorkbookBySlug(slug)?.subtitle ?? '', anchor: '' };

/**
 * Reader intent → workbook. Matched against an article's slug, title and
 * tags so the CTA answers the question the article raises, instead of
 * rotating through the category.
 */
const INTENT_RULES: { slug: string; patterns: RegExp }[] = [
  {
    slug: 'family-nomad-life',
    patterns: /\b(family|families|kids?|child(ren)?|school(ing)?|homeschool|baby|toddler|teen)\b/,
  },
  {
    slug: 'the-nomad-money-reset',
    patterns:
      /\b(cost|costs|costly|budget|budgeting|money|price|prices|pricing|cheap|afford|expense|expenses|spend|spending|financ|insurance|savings|runway|break-even|hidden-costs)\b/,
  },
  {
    slug: 'work-from-anywhere-for-real',
    patterns:
      /\b(work|working|remote|job|jobs|income|earn|earning|freelance|business|wifi|internet|connectivity|starlink|signal|coworking|digital-nomad)\b/,
  },
  {
    slug: 'the-build-before-the-build',
    patterns:
      /\b(build|building|conversion|convert|layout|insulat\w*|wiring|electric\w*|solar|battery|batteries|inverter|plumbing|water-system|greywater|ventilation|diy|install\w*)\b/,
  },
  {
    slug: 'choose-your-rig',
    patterns:
      /\b(choose|choosing|which|best|buy|buying|compare|comparison|van|vans|sprinter|bus|rv|truck|trailer|boat|catamaran|sailboat|cabin|tiny-house|payload|weight|model|models)\b/,
  },
  {
    slug: 'small-space-real-life',
    patterns:
      /\b(storage|organis\w*|organiz\w*|laundry|cooking|kitchen|shower|toilet|hygiene|minimalis\w*|declutter|routine|daily|space|sleep|bed|winter|cold|heat)\b/,
  },
  {
    slug: 'is-nomad-living-right-for-you',
    patterns:
      /\b(beginner|start|starting|getting-started|guide|basics|is-it|worth-it|realit\w*|pros?-and-cons?|myth|mistake|quit|regret|first-time|considering)\b/,
  },
];

export interface ArticleIntentInput {
  slug?: string;
  title?: string;
  tags?: string[];
  category?: string;
}

/**
 * Ranked intent matches for an article, best first, deduplicated and
 * padded with the category fallback so an offset always resolves.
 */
export const rankWorkbooksForArticle = (article: ArticleIntentInput): Workbook[] => {
  const haystack = [article.slug ?? '', article.title ?? '', ...(article.tags ?? [])]
    .join(' ')
    .toLowerCase()
    .replace(/\s+/g, '-');

  const matched = INTENT_RULES.filter((rule) => rule.patterns.test(haystack))
    .map((rule) => getWorkbookBySlug(rule.slug))
    .filter((w): w is Workbook => Boolean(w));

  const categoryMatches = workbooks.filter((w) =>
    w.matches.includes(article.category ?? ''),
  );

  const ordered: Workbook[] = [];
  for (const w of [...matched, ...categoryMatches, ...workbooks]) {
    if (!ordered.some((existing) => existing.slug === w.slug)) ordered.push(w);
  }
  return ordered;
};

export const getWorkbookForArticle = (
  article: ArticleIntentInput,
  offset = 0,
): Workbook => {
  const ranked = rankWorkbooksForArticle(article);
  return ranked[offset % ranked.length];
};

/**
 * The action layer: the seven questions a reader works through, in order,
 * mapped to the title that answers each one.
 */
export const DECISION_JOURNEY: { question: string; slug: string }[] = [
  { question: 'Is this life right for me?', slug: 'is-nomad-living-right-for-you' },
  { question: 'What should I live in?', slug: 'choose-your-rig' },
  { question: 'How should I build it?', slug: 'the-build-before-the-build' },
  { question: 'Can I afford this?', slug: 'the-nomad-money-reset' },
  { question: 'Can I live in the space day after day?', slug: 'small-space-real-life' },
  { question: 'Can I earn from the road?', slug: 'work-from-anywhere-for-real' },
  { question: 'Can my family make it work?', slug: 'family-nomad-life' },
];
